<template>
  <SFFooterSection
    :title="$t('footer.payment_methods')"
    content-class="flex flex-wrap items-center gap-2"
  >
    <template v-if="payments && payments.length > 0">
      <div
        v-for="payment in payments"
        :key="payment.id"
        class="flex h-10 w-16 items-center justify-center overflow-hidden text-xs text-white"
        :title="payment.name"
      >
        <!-- Display payment icon using CDN URL for static assets -->
        <img
          v-if="getPaymentIconHash(payment)"
          :src="getPaymentIconUrl(payment)"
          :alt="payment.name"
          class="h-10 w-16 object-contain"
          loading="lazy"
        />
        <span v-else>
          {{ payment.name.substring(0, 2).toUpperCase() }}
        </span>
      </div>
    </template>
    <template v-else>
      <!-- Fallback: show placeholder payment methods -->
      <div
        v-for="n in 6"
        :key="`pay-${n}`"
        class="h-8 w-14 rounded bg-gray-600"
      />
    </template>
  </SFFooterSection>
</template>

<script setup lang="ts">
import { defineAsyncComponent } from 'vue'
import type { NavigationTreeItem } from '@scayle/storefront-nuxt'
import type { Payment } from '~/types/payment'
import { useRuntimeConfig } from '#app'

const SFFooterSection = defineAsyncComponent(
  () => import('./SFFooterSection.vue'),
)

defineOptions({
  name: 'SFFooterPaymentSection',
})

defineProps<{
  payments?: NavigationTreeItem[] | Payment[]
}>()

const config = useRuntimeConfig()

const getPaymentIconHash = (
  payment: NavigationTreeItem | Payment,
): string | undefined => {
  // Handle NavigationTreeItem assets
  if ('assets' in payment && payment.assets) {
    const assets = Object.values(payment.assets)
    const assetPath = assets[0] as string
    return assetPath
  }

  // Handle Payment type icon
  if ('icon' in payment && payment.icon) {
    return payment.icon
  }

  return undefined
}

const getPaymentIconUrl = (
  payment: NavigationTreeItem | Payment,
): string | undefined => {
  const iconHash = getPaymentIconHash(payment)
  if (!iconHash) {
    return undefined
  }

  // Use CDN URL instead of Cloudinary for the same Contentful path
  const cdnUrl = config.public.cdnUrl || 'https://snipes-test.cdn.scayle.cloud'

  // Keep the original path from Contentful, just replace the base URL
  return `${cdnUrl}/${iconHash}`
}
</script>
